import { ArrowLeft } from "lucide-react";

/**
 * Terms of Use and Privacy Policy.
 *
 * These are original drafts written for a recording product, not adapted
 * from another company's documents. They describe how Dicta actually
 * behaves today — including environment-specific availability terms and that
 * transcripts are machine-generated and reviewed, never certified. They still need
 * review by a qualified lawyer before Dicta is offered publicly.
 */

const UPDATED = "31 July 2026";
const STAGING_BUILD = import.meta.env.MODE === "staging";

export type LegalDocument = "terms" | "privacy" | "faq";

type LegalPageProps = {
  document: LegalDocument;
  onBack: () => void;
};

export function LegalPage({ document, onBack }: LegalPageProps) {
  return (
    <main className="legal-page" id="main-content" tabIndex={-1}>
      <div className="legal-page__inner">
        <button className="button button--text" type="button" onClick={onBack}>
          <ArrowLeft size={15} aria-hidden="true" />
          Back to Dicta
        </button>

        {document === "terms" ? (
          <Terms />
        ) : document === "privacy" ? (
          <Privacy />
        ) : (
          <Faq />
        )}

        <footer className="legal-page__footer">
          <a href="/faq">FAQ</a>
          <span aria-hidden="true">·</span>
          <a href="/terms">Terms of Use</a>
          <span aria-hidden="true">·</span>
          <a href="/privacy">Privacy Policy</a>
        </footer>
      </div>
    </main>
  );
}

function Faq() {
  return (
    <article className="legal-prose">
      <p className="overline">Help</p>
      <h1>Frequently asked questions</h1>

      <h2>Who can access this recording?</h2>
      <p>
        Only the account that created it, plus recipients the creator chooses
        to deliver it to. Recorded participants do not automatically receive
        access. See the <a href="/privacy">Privacy Policy</a>.
      </p>

      <h2>Is it used for model training?</h2>
      <p>
        No. Audio sent through the OpenAI API is not used to train OpenAI
        models, and Dicta does not use recordings or transcripts for model
        training.
      </p>

      <h2>Where is it processed?</h2>
      <p>
        Dicta runs on Cloudflare&apos;s network. Speech processing uses OpenAI
        in the United States. Stored application data uses Cloudflare R2 and
        D1.
      </p>

      <h2>Can it be permanently deleted?</h2>
      <p>
        Yes. You can opt in before recording to have source audio deleted within
        24 hours after recording ends. Dicta waits for successful transcription
        and does not override a legal hold. Audit and integrity records are
        retained for the life of the transcript.
      </p>

      <h2>Does Dicta claim certification?</h2>
      <p>
        No. Every export is marked <strong>Reviewed — Not Certified</strong>.
        Dicta is never a substitute for an official reporter where one is
        required.
      </p>

      <h2>How are consent and recording authority handled?</h2>
      <p>
        Recording Details contains authority attestations. Start is also
        blocked by a consent dialog, and the user&apos;s confirmations are
        logged with the recording.
      </p>
    </article>
  );
}

function Terms() {
  return (
    <article className="legal-prose">
      <p className="overline">Legal</p>
      <h1>Terms of Use</h1>
      <p className="legal-updated">Last updated {UPDATED}</p>

      <p>
        These terms govern your use of Dicta, a service for recording,
        transcribing, reviewing and issuing transcripts of legal proceedings.
        By using Dicta you agree to them. If you are agreeing on behalf of a
        firm or organisation, you confirm you are authorised to bind it.
      </p>

      <h2>1. What Dicta is, and what it is not</h2>
      <p>
        Dicta produces a machine-generated transcript that you review and
        correct. Every export is marked <strong>Reviewed — Not Certified</strong>.
        Dicta is not a court reporting service, does not provide a certified
        or sworn transcript, and is not a substitute for an official reporter
        where one is required. Dicta does not provide legal advice.
      </p>
      <p>
        Automatic speech recognition makes mistakes, including on names,
        figures, dates and overlapping speech. You are responsible for
        reviewing a transcript against the source audio before you rely on it
        or give it to anyone else.
      </p>

      <h2>2. Your authority to record</h2>
      <p>
        You are solely responsible for having the legal right to record each
        session. Recording laws differ between jurisdictions: some require the
        consent of every participant, some require only one party's consent,
        and some proceedings additionally require the permission of the court
        or tribunal.
      </p>
      <p>
        Before each recording you must confirm that you are authorised, that
        every participant has been told the session is being recorded, and
        that any additional approval required for that proceeding type is
        held. Dicta records these confirmations, but making them accurately is
        your obligation. Do not use Dicta to record covertly.
      </p>

      <h2>3. Your account</h2>
      <p>
        Keep your sign-in credentials confidential and tell us promptly if you
        believe your account has been used without your authority. You are
        responsible for activity carried out through your account. You must be
        at least 18 years old.
      </p>

      <h2>4. Your content</h2>
      <p>
        Audio you capture, transcripts generated from it, and edits you make
        remain yours. You grant us only the permission needed to run the
        service for you: to store your recordings, process them into
        transcripts, and produce the exports you request. We do not use your
        recordings or transcripts to train machine learning models.
      </p>
      <p>
        You must not upload content you have no right to record or share, and
        you must not use Dicta unlawfully or to infringe anyone's rights.
      </p>

      <h2>5. Service providers</h2>
      <p>
        Dicta uses third-party speech recognition to produce transcripts, and
        cloud infrastructure to run the service and store data. These
        providers act on our instructions and are bound by confidentiality
        obligations. See the <a href="/privacy">Privacy Policy</a> for who
        they are and what they receive.
      </p>

      <h2>6. {STAGING_BUILD ? "Availability, and this staging build" : "Availability"}</h2>
      {STAGING_BUILD ? (
        <p>
          This is a staging environment provided for evaluation. It may change
          or be reset without notice, and data in it may be deleted. Do not use
          this build for a live proceeding you cannot afford to lose, and do not
          treat it as a system of record.
        </p>
      ) : (
        <p>
          Dicta is an online service and may change or occasionally be unavailable.
          Keep independent copies of material you cannot afford to lose, review every
          transcript before relying on it, and do not treat an unreviewed transcript
          as a system of record.
        </p>
      )}

      <h2>7. Liability</h2>
      <p>
        To the fullest extent the law allows, Dicta is provided as is, and we
        exclude implied warranties. We are not liable for indirect or
        consequential loss, or for loss arising from your reliance on an
        unreviewed transcript, from recording without proper authority, or
        from the unavailability of {STAGING_BUILD ? "this staging service" : "the service"}.
      </p>
      <p>
        Nothing in these terms excludes liability that cannot lawfully be
        excluded. If you are in Australia, nothing here limits rights you have
        under the Australian Consumer Law.
      </p>

      <h2>8. Ending your use</h2>
      <p>
        You may stop using Dicta at any time and ask us to delete your data. We
        may suspend an account that is being used unlawfully or in a way that
        risks other users or the service.
      </p>

      <h2>9. Changes</h2>
      <p>
        We may update these terms. If a change materially affects you we will
        make reasonable efforts to tell you before it takes effect. Continuing
        to use Dicta after a change means you accept the updated terms.
      </p>

      <h2>10. Contact</h2>
      <p>
        Questions about these terms: <a href="mailto:info@dicta.io">info@dicta.io</a>.
      </p>
    </article>
  );
}

function Privacy() {
  return (
    <article className="legal-prose">
      <p className="overline">Legal</p>
      <h1>Privacy Policy</h1>
      <p className="legal-updated">Last updated {UPDATED}</p>

      <p>
        This policy explains what Dicta collects, why, who it is shared with,
        and the choices you have. Dicta handles recordings of legal
        proceedings, which frequently contain sensitive information about
        people who are not our users — we treat that as the core obligation of
        the product.
      </p>

      <h2>What we collect</h2>
      <h3>Information you give us</h3>
      <ul>
        <li>
          <strong>Account details</strong> — your email address, and the plan
          you are on.
        </li>
        <li>
          <strong>Matter details</strong> — the title, reference, venue,
          jurisdiction, participant names and roles, and any glossary terms
          you enter.
        </li>
        <li>
          <strong>Recordings</strong> — the audio captured during a session,
          in both its original compressed form and a processing copy.
        </li>
        <li>
          <strong>Transcripts and edits</strong> — generated text, every
          correction you make, the reason you give for it, comments,
          bookmarks, and who made each change.
        </li>
      </ul>

      <h3>Information we generate</h3>
      <ul>
        <li>
          <strong>Authority records</strong> — your confirmations that you
          were authorised to record and that participants were notified, with
          the time you made them.
        </li>
        <li>
          <strong>Audit events</strong> — a log of significant actions such as
          creating, finalising, freezing, exporting and delivering a
          transcript.
        </li>
        <li>
          <strong>Integrity data</strong> — cryptographic hashes of the audio,
          so a transcript can be shown to correspond to the recording it came
          from.
        </li>
        <li>
          <strong>Session data</strong> — a hashed session identifier and
          sign-in timestamps, and hashed IP addresses used to rate-limit
          sign-in attempts.
        </li>
      </ul>

      <h2>Why we use it</h2>
      <p>
        To run the service: capture audio, transcribe it, let you review and
        correct it, produce exports, and deliver them to recipients you
        nominate. To keep the service secure, including detecting and limiting
        abusive sign-in attempts. To maintain the evidentiary record that
        makes a transcript defensible. And to meet legal obligations that
        apply to us.
      </p>
      <p>
        We do not sell your data, we do not use it for advertising, and we do
        not use your recordings or transcripts to train machine learning
        models.
      </p>

      <h2>Who we share it with</h2>
      <ul>
        <li>
          <strong>OpenAI</strong> — receives recording audio to produce
          transcripts. Audio submitted through their API is not used to train
          their models.
        </li>
        <li>
          <strong>Cloudflare</strong> — hosts the service and stores the
          database and audio.
        </li>
        <li>
          <strong>Resend</strong> — sends delivery emails, when you choose to
          deliver a transcript by email. It receives the recipient address and
          the message.
        </li>
        <li>
          <strong>Recipients you choose</strong> — anyone you send a transcript
          to, through a secure link or an encrypted attachment.
        </li>
      </ul>
      <p>
        We may also disclose information if we are legally required to, or to
        establish or defend legal claims.
      </p>

      <h2>People who are recorded but are not users</h2>
      <p>
        Participants in a recording usually have no account with us. Our
        relationship is with the user who made the recording, and that user is
        responsible for having the authority to record and for telling
        participants. If you are a participant and want to know what is held
        about you, contact the firm or person who recorded the session; you
        may also contact us and we will try to help identify the right
        controller.
      </p>

      <h2>How long we keep it</h2>
      <p>
        Recordings and transcripts are kept for as long as your account is
        active, or until you delete them. The processing copy of audio is
        deleted once a transcript is finalised. If automatic audio deletion was
        enabled when a recording was created, its source audio is scheduled for
        deletion within 24 hours after recording ends; incomplete transcription
        or a legal hold delays deletion. Audit and integrity records are kept for
        the life of the transcript, because deleting them would defeat their
        purpose.
      </p>
      <p>
        Ask us to delete your data and we will do so within 30 days, except
        where we must keep something to meet a legal obligation.
      </p>

      <h2 id="security">Security</h2>
      <p>
        Audio is encrypted in your browser before it is buffered locally, and
        is encrypted in transit and at rest on our infrastructure. Sessions
        use HttpOnly, Secure cookies and expire automatically. Access to a
        matter is limited to the account that created it. Sign-in attempts are
        rate limited. No system is perfectly secure, and we will tell affected
        users promptly if a breach occurs that is likely to put them at risk.
      </p>

      <h2>Your rights</h2>
      <p>
        Depending on where you live, you may have the right to access a copy
        of your data, correct it, delete it, restrict or object to processing,
        or receive it in a portable format. To exercise any of these, email us.
        If you are in Australia and are not satisfied with our response, you
        may complain to the Office of the Australian Information Commissioner;
        in the EEA or UK, to your local supervisory authority.
      </p>

      <h2>Where data is held</h2>
      <p>
        Dicta runs on distributed infrastructure, and data may be processed in
        countries other than your own, including the United States. Where data
        leaves Australia, the EEA or the UK, we rely on the transfer
        protections our providers have in place.
      </p>

      <h2>Changes</h2>
      <p>
        We will post any update here and change the date above. Material
        changes will be notified to account holders.
      </p>

      <h2>Contact</h2>
      <p>
        Privacy questions or requests:{" "}
        <a href="mailto:info@dicta.io">info@dicta.io</a>.
      </p>
    </article>
  );
}
